/**
 * DataDiscoveryModal
 *
 * Tabbed data management hub with Schemas, Templates, and AI Create tabs.
 * - Schemas: data source discovery (DataDiscoveryCard / SchemaDetailCard)
 * - Templates: full CRUD with folder organization, search/filter, sort
 * - Create: AI-powered template builder via NL query pipeline
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Database,
  FileText,
  Sparkles,
  Save,
  ArrowUp,
  RotateCcw,
  ChevronRight,
  Plus,
  GitBranch,
  Settings2,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { DataDiscoveryCard } from './DataDiscoveryCard';
import { WorkflowDataSourceTab } from './WorkflowDataSourceTab';
import { SchemaDetailCard } from './SchemaDetailCard';
import { TemplateManagementTab } from './TemplateManagementTab';
import { CompositeQueryBuilderTab } from './CompositeQueryBuilderTab';
import { VoiceMicButton } from './VoiceMicButton';
import { useVoiceControl } from '@/hooks/useVoiceControl';
import {
  dataApi,
  type ClarificationResponsePayload,
} from '@/services/api/dataApi';
import {
  executeCompositeQueryDefinition,
  queryTemplateApi,
} from '@/services/api/queryTemplateApi';
import type {
  AggregationFunction,
  CompositeQueryDefinition,
  DataDiscoveryResult,
  SchemaPreview,
  TimeGranularity,
} from '@/types/chartDataSource';
import type { Component } from '@/types';
import type { UseDataDiscoveryReturn } from './useDataDiscovery';
import {
  buildCreateClarificationResponsePayload,
  buildCreateClarificationResumePrompt,
  generateCreateTemplateResult,
  getCreateClarificationSourceHint,
  getDisplayResultTypeLabel,
  type CreateTemplateResult,
} from './DataDiscoveryModal.createLogic';
import {
  buildCreateCompositePreviewModel,
  buildCreateTemplatePreviewModel,
  formatCreatePreviewValue,
  normalizeCreatePreviewRow,
  type CreatePreviewModel,
} from './DataDiscoveryModal.previewModel';
import {
  buildGeneratedCreateApplyPayload,
  buildGeneratedCreateSavePayload,
  getGeneratedCreateActionState,
  PERSONAL_SPACE_SINGLE_SOURCE_SCOPE_ERROR,
  type GeneratedCreateActionOptions,
} from './DataDiscoveryModal.generatedActions';
import {
  buildManualDraftFromGeneratedCreateResult,
  buildManualCreateTemplateResult,
  updateManualSingleSourceDraftClearingPreview,
  type ManualCreateDraft,
  type ManualSingleSourceQueryDraft,
} from './DataDiscoveryModal.manualModel';
import {
  buildTemplateSaveMetadataFromCreateResult,
  EMPTY_TEMPLATE_SAVE_METADATA,
  type TemplateSaveMetadata,
} from './DataDiscoveryModal.saveMetadata';
import {
  TemplateSaveMetadataFields,
  type TemplateSaveMetadataField,
} from './TemplateSaveMetadataFields';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { useEditorStore } from '@/stores/editor/editor-store';
import { useViewerStore } from '@/stores/viewer/viewer-store';
import { CsvDatasetImportPanel } from '@/components/data/CsvDatasetImportPanel';
import type { CsvDatasetImportResult } from '@/services/api/csvDatasetImportApi';

const createSaveInputClass =
  'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const manualSelectClass =
  'w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm text-foreground ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

const manualAggregationOptions: Array<Exclude<AggregationFunction, 'none'>> = [
  'sum',
  'avg',
  'min',
  'max',
  'count',
  'last',
  'first',
];
const manualTimeGranularityOptions: TimeGranularity[] = ['minute', 'hour', 'day', 'week', 'month'];
const manualTimeRangePresetOptions = ['last_hour', 'last_24h', 'last_7d', 'last_30d', 'today', 'yesterday'];
const emptyCreateSaveForm = () => ({ ...EMPTY_TEMPLATE_SAVE_METADATA });
const snapshotTemplateSaveMetadata = (metadata: TemplateSaveMetadata): TemplateSaveMetadata => ({ ...metadata });
type ActiveTab = 'templates' | 'schemas' | 'workflow' | 'import' | 'create';

function createEmptyManualSingleSourceDraft(): ManualSingleSourceQueryDraft {
  return {
    kind: 'single-source',
    source: null,
    measures: [{ aggregation: 'sum' }],
    timeRange: { preset: 'last_24h' },
    limit: 100,
  };
}

function getSchemaLabel(schema: SchemaPreview): string {
  return schema.displayName || schema.name;
}

function getManualSchemaOptions(discoveryResult: DataDiscoveryResult | null): SchemaPreview[] {
  return discoveryResult?.categories.flatMap((category) => category.schemas) ?? [];
}

function getSchemaFieldOptions(schema: SchemaPreview | undefined): string[] {
  return schema?.fields ?? [];
}

function fieldLooksLikeTime(field: string): boolean {
  return /time|timestamp|datetime|date|ts\b/i.test(field);
}

function getCurrentRouteOrgSlug(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const match = window.location.pathname.match(/^\/orgs\/([^/]+)(?:\/|$)/);
  if (!match?.[1]) {
    return null;
  }

  const decodedSlug = decodeURIComponent(match[1]).trim();
  return decodedSlug.length > 0 ? decodedSlug : null;
}

function getTrimmedString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function isActiveTab(value: string): value is ActiveTab {
  switch (value) {
    case 'templates':
    case 'schemas':
    case 'workflow':
    case 'import':
    case 'create':
      return true;
    default:
      return false;
  }
}

function fieldLooksLikeIdentifier(field: string): boolean {
  return /(^id$|_id$|id$|name|label|status|state)/i.test(field);
}

function inferInitialTimeField(fields: string[]): string | undefined {
  return fields.find(fieldLooksLikeTime);
}

function inferInitialMeasureField(fields: string[]): string | undefined {
  return fields.find((field) => !fieldLooksLikeTime(field) && !fieldLooksLikeIdentifier(field)) ?? fields[0];
}

function buildManualAlias(aggregation: string | undefined, field: string | undefined): string | undefined {
  if (!aggregation || !field) {
    return undefined;
  }

  return `${aggregation}_${field.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_+|_+$/g, '')}`;
}

function getManualCompositeDefinition(draft: ManualCreateDraft | null): CompositeQueryDefinition | null {
  if (!draft || draft.kind === 'single-source') {
    return null;
  }

  const conversion = buildManualCreateTemplateResult(draft);
  return conversion.ok && conversion.result.kind === 'composite'
    ? conversion.result.definition
    : null;
}

async function previewCompositeRows(definition: CompositeQueryDefinition): Promise<Record<string, unknown>[]> {
  const preview = await executeCompositeQueryDefinition({
    ...definition,
    limit: 5,
  });

  return Array.isArray(preview.data)
    ? preview.data.slice(0, 5).flatMap((row) => {
        const normalizedRow = normalizeCreatePreviewRow(row);
        return normalizedRow ? [normalizedRow] : [];
      })
    : [];
}

function canAcceptCreateReview(result: CreateTemplateResult): boolean {
  return result.metadata.needsReview && result.metadata.reviewAcceptanceAllowed !== false;
}

function getCreateActionButtonClass(primary: boolean): string {
  return primary
    ? 'inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed'
    : 'inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:border-gray-600 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700/50';
}

function PreviewDetail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-xs font-medium text-gray-800 dark:text-gray-200">{value}</dd>
    </div>
  );
}

function uniquePreviewLabels(labels: string[]): string[] {
  return Array.from(new Set(labels.filter((label) => label.trim().length > 0)));
}

function PreviewSummary({
  summary,
  labels,
  tone = 'neutral',
}: {
  summary: string | null;
  labels: string[];
  tone?: 'source' | 'field' | 'neutral';
}) {
  return (
    <div className="space-y-1.5">
      {summary && <p>{summary}</p>}
      <PreviewPills labels={labels} tone={tone} />
    </div>
  );
}

function PreviewPills({ labels, tone = 'neutral' }: { labels: string[]; tone?: 'source' | 'field' | 'neutral' }) {
  if (labels.length === 0) {
    return <span className="text-xs text-gray-400 dark:text-gray-500">None selected</span>;
  }

  const toneClass =
    tone === 'source'
      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      : tone === 'field'
        ? 'border border-emerald-200 bg-white text-gray-700 dark:border-emerald-800 dark:bg-gray-900 dark:text-gray-300'
        : 'border border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300';

  return (
    <div className="flex flex-wrap gap-1.5">
      {labels.map((label) => (
        <span key={label} className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${toneClass}`}>
          {label}
        </span>
      ))}
    </div>
  );
}

function getPreviewFactClass(tone: CreatePreviewModel['feedbackFacts'][number]['tone']): string {
  return tone === 'warning'
    ? 'border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
    : 'border-emerald-200 bg-white text-emerald-800 dark:border-emerald-800 dark:bg-gray-900 dark:text-emerald-300';
}

function SkeletonBlock({ className }: { className: string }) {
  return <div className={`rounded bg-gray-200/80 dark:bg-gray-700/60 ${className}`} />;
}

function CreateResultPreviewSkeleton() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label="Creating template preview"
      data-testid="create-preview-skeleton"
      className="space-y-4"
    >
      <div className="rounded-md bg-gray-50 p-3 dark:bg-gray-800/50">
        <div className="animate-pulse space-y-2">
          <SkeletonBlock className="h-3 w-4/5" />
          <SkeletonBlock className="h-3 w-2/3" />
        </div>
      </div>

      <div className="rounded-md border border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/20">
        <div className="border-b border-emerald-200 px-3 py-2 dark:border-emerald-800">
          <div className="flex items-center gap-2">
            <SkeletonBlock className="h-4 w-36 animate-pulse" />
            <SkeletonBlock className="h-5 w-20 rounded-full animate-pulse" />
          </div>
        </div>

        <div className="space-y-3 px-3 py-3">
          <div className="flex flex-wrap gap-1.5">
            <SkeletonBlock className="h-5 w-28 rounded-full animate-pulse" />
            <SkeletonBlock className="h-5 w-24 rounded-full animate-pulse" />
            <SkeletonBlock className="h-5 w-32 rounded-full animate-pulse" />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {['source', 'schema', 'fields', 'time'].map((item) => (
              <div key={item} className="space-y-1.5">
                <SkeletonBlock className="h-2.5 w-20 animate-pulse" />
                <div className="flex flex-wrap gap-1.5">
                  <SkeletonBlock className="h-5 w-24 rounded-full animate-pulse" />
                  <SkeletonBlock className="h-5 w-16 rounded-full animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-emerald-200 px-3 py-2 dark:border-emerald-800">
          <SkeletonBlock className="mb-2 h-3 w-28 animate-pulse" />
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-3">
              <SkeletonBlock className="h-3 animate-pulse" />
              <SkeletonBlock className="h-3 animate-pulse" />
              <SkeletonBlock className="h-3 animate-pulse" />
            </div>
            {[0, 1, 2].map((row) => (
              <div key={row} className="grid grid-cols-3 gap-3">
                <SkeletonBlock className="h-3 animate-pulse" />
                <SkeletonBlock className="h-3 animate-pulse" />
                <SkeletonBlock className="h-3 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreateResultPreviewCard({
  previewModel,
  needsReview,
}: {
  previewModel: CreatePreviewModel;
  needsReview: boolean;
}) {
  const noteLabels = uniquePreviewLabels(
    [previewModel.reviewNote, previewModel.confidenceNote].filter(
      (note): note is string => typeof note === 'string' && note.trim().length > 0,
    ),
  );
  const fieldAndSeriesLabels = uniquePreviewLabels([...previewModel.fieldLabels, ...previewModel.seriesLabels]);

  return (
    <>
      <div className="px-3 py-2 border-b border-emerald-200 dark:border-emerald-800">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-xs font-semibold text-emerald-800 dark:text-emerald-300 flex items-center gap-1.5">
            <ChevronRight className="h-3.5 w-3.5" />
            Result type: {previewModel.displayResultTypeLabel}
          </h4>
          {needsReview && (
            <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
              Needs Review
            </span>
          )}
        </div>
      </div>

      <div className="px-3 py-3 space-y-3">
        {previewModel.feedbackFacts.length > 0 && (
          <div className="flex flex-wrap gap-1.5" aria-label="Generated result summary">
            {previewModel.feedbackFacts.map((fact) => (
              <span
                key={fact.id}
                className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${getPreviewFactClass(fact.tone)}`}
              >
                {fact.label}
              </span>
            ))}
          </div>
        )}

        {noteLabels.length > 0 && (
          <div className="space-y-1">
            {noteLabels.map((note) => (
              <p key={note} className="text-xs text-amber-700 dark:text-amber-300">
                {note}
              </p>
            ))}
          </div>
        )}

        <dl className="grid gap-3 sm:grid-cols-2">
          <PreviewDetail
            label="Data sources"
            value={
              <PreviewSummary summary={previewModel.sourceSummary} labels={previewModel.sourceLabels} tone="source" />
            }
          />
          <PreviewDetail
            label="Schema"
            value={
              <PreviewSummary summary={previewModel.schemaSummary} labels={previewModel.schemaLabels} tone="neutral" />
            }
          />
          <PreviewDetail
            label="Selected fields / chart series"
            value={<PreviewPills labels={fieldAndSeriesLabels} tone="field" />}
          />
          {previewModel.timeRangeLabel && <PreviewDetail label="Time range" value={previewModel.timeRangeLabel} />}
          {previewModel.limitLabel && <PreviewDetail label="Limit" value={previewModel.limitLabel} />}
          {previewModel.joinSummary && <PreviewDetail label="Join strategy" value={previewModel.joinSummary} />}
          {previewModel.matchingSummary && (
            <PreviewDetail label="Matching strategy" value={previewModel.matchingSummary} />
          )}
        </dl>
      </div>

      {previewModel.previewRows.length > 0 && previewModel.previewColumns.length > 0 && (
        <div className="border-t border-emerald-200 dark:border-emerald-800 px-3 py-2">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
            Sample rows ({previewModel.previewRows.length} row
            {previewModel.previewRows.length !== 1 ? 's' : ''})
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  {previewModel.previewColumns.map((key) => (
                    <th key={key} className="text-left py-1 pr-3 font-medium text-gray-600 dark:text-gray-400">
                      {previewModel.previewColumnLabels[key] ?? key}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewModel.previewRows.slice(0, 5).map((row, i) => (
                  <tr key={i} className="border-b border-gray-100 dark:border-gray-700">
                    {previewModel.previewColumns.map((key) => (
                      <td key={key} className="py-1 pr-3 text-gray-500 dark:text-gray-400">
                        {formatCreatePreviewValue(row[key]).substring(0, 30)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Modal component
// ---------------------------------------------------------------------------

interface DataDiscoveryModalProps extends UseDataDiscoveryReturn {
  component?: Component | null;
}

export function DataDiscoveryModal({
  isOpen,
  isLoading,
  error,
  discoveryResult,
  schemaExplorationResult,
  closeModal,
  openModal,
  handleSelectSchema,
  handleSelectTemplateField,
  handleSelectTemplate,
  handleSuggestedQuery,
  handleUseSchema,
  handleSelectWorkflow,
  handleSelectComposite,
  handleImportedCsvDatasetResult,
  handleApplyQueryDefinition,
  handleBackToDiscovery,
  component,
}: DataDiscoveryModalProps) {
  const [activeTab, setActiveTab] = useState<ActiveTab>('templates');

  // --- Create tab state ---
  const [createMode, setCreateMode] = useState<'ai' | 'manual'>('ai');
  const [createInput, setCreateInput] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [createResult, setCreateResult] = useState<CreateTemplateResult | null>(null);
  const [createReviewAccepted, setCreateReviewAccepted] = useState(false);
  const [createClarificationSelectedValue, setCreateClarificationSelectedValue] = useState<string | null>(null);
  const [createClarificationFreeText, setCreateClarificationFreeText] = useState('');
  const [manualSingleSourceDraft, setManualSingleSourceDraft] = useState<ManualSingleSourceQueryDraft>(
    createEmptyManualSingleSourceDraft,
  );
  const [manualImportedDraft, setManualImportedDraft] = useState<ManualCreateDraft | null>(null);
  const [manualImportedCompositeDefinition, setManualImportedCompositeDefinition] =
    useState<CompositeQueryDefinition | null>(null);
  const [manualImportedSaveMetadata, setManualImportedSaveMetadata] = useState<TemplateSaveMetadata | undefined>();
  const [manualImportNotice, setManualImportNotice] = useState<string | null>(null);
  const [manualImportVersion, setManualImportVersion] = useState(0);
  const createInputRef = useRef<HTMLTextAreaElement>(null);
  const [createSaveForm, setCreateSaveForm] = useState(emptyCreateSaveForm);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const handleCreateSaveMetadataFieldChange = useCallback(
    (field: TemplateSaveMetadataField, value: string) => {
      setCreateSaveForm((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );
  const isPersonalSpace = useOrganizationStore((state) => state.currentContext?.type === 'user');
  const currentContext = useOrganizationStore((state) => state.currentContext);
  const pendingRestoredOrganizationContext = useOrganizationStore(
    (state) => state.pendingRestoredOrganizationContext,
  );
  const organizations = useOrganizationStore((state) => state.organizations);
  const allOrganizations = useOrganizationStore((state) => state.allOrganizations);
  const memberOrganizations = useMemo(
    () => (Array.isArray(organizations) ? organizations : []),
    [organizations],
  );
  const platformOrganizations = useMemo(
    () => (Array.isArray(allOrganizations) ? allOrganizations : []),
    [allOrganizations],
  );
  const platformAdminEntitlement = useOrganizationStore((state) => state.platformAdminEntitlement);
  const getOrganization = useOrganizationStore((state) => state.getOrganization);
  const currentProject = useEditorStore((state) => state.project);
  const viewerOrganizationId = useViewerStore((state) => state.viewerOrgId);
  const [resolvedRouteOrganizationId, setResolvedRouteOrganizationId] = useState<string | null>(null);
  const routeOrganizationSlug = getCurrentRouteOrgSlug();
  const importOrganizationContext =
    pendingRestoredOrganizationContext?.type === 'organization'
      ? pendingRestoredOrganizationContext
      : currentContext?.type === 'organization'
        ? currentContext
        : null;
  const importVisibleOrganizations = useMemo(() => {
    return platformAdminEntitlement
      ? [...memberOrganizations, ...platformOrganizations]
      : memberOrganizations;
  }, [memberOrganizations, platformAdminEntitlement, platformOrganizations]);
  const routeOrganizationId = useMemo(() => {
    if (routeOrganizationSlug === null) {
      return null;
    }

    const matchedRouteOrganization = importVisibleOrganizations.find(
      (organization) => organization.slug === routeOrganizationSlug,
    );
    if (matchedRouteOrganization) {
      return matchedRouteOrganization._id;
    }

    if (pendingRestoredOrganizationContext?.type === 'organization') {
      const pendingSlug =
        typeof pendingRestoredOrganizationContext.orgSlug === 'string'
          ? pendingRestoredOrganizationContext.orgSlug.trim()
          : '';
      if (pendingSlug === routeOrganizationSlug) {
        return pendingRestoredOrganizationContext.id;
      }
    }

    if (currentContext?.type === 'organization') {
      const currentSlug = typeof currentContext.orgSlug === 'string' ? currentContext.orgSlug.trim() : '';
      if (currentSlug === routeOrganizationSlug) {
        return currentContext.id;
      }
    }

    return null;
  }, [
    currentContext,
    importVisibleOrganizations,
    pendingRestoredOrganizationContext,
    routeOrganizationSlug,
  ]);
  useEffect(() => {
    let cancelled = false;

    if (routeOrganizationSlug === null) {
      setResolvedRouteOrganizationId(null);
      return () => {
        cancelled = true;
      };
    }

    if (routeOrganizationId !== null) {
      setResolvedRouteOrganizationId(routeOrganizationId);
      return () => {
        cancelled = true;
      };
    }

    void getOrganization(routeOrganizationSlug)
      .then((organization) => {
        if (!cancelled) {
          const nextOrganizationId =
            typeof organization._id === 'string' && organization._id.trim().length > 0
              ? organization._id
              : null;
          setResolvedRouteOrganizationId(nextOrganizationId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedRouteOrganizationId(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getOrganization, routeOrganizationId, routeOrganizationSlug]);
  const requestingOrganizationId = useMemo(() => {
    if (importOrganizationContext === null) {
      return null;
    }

    const matchedOrganization =
      importVisibleOrganizations.find((organization) => organization._id === importOrganizationContext.id) ??
      (typeof importOrganizationContext.orgSlug === 'string' && importOrganizationContext.orgSlug.trim().length > 0
        ? importVisibleOrganizations.find(
            (organization) => organization.slug === importOrganizationContext.orgSlug,
          )
        : undefined);

    return matchedOrganization?._id ?? importOrganizationContext.id;
  }, [importOrganizationContext, importVisibleOrganizations]);
  const projectOrganizationId =
    currentProject?.owner_type === 'Organization' &&
    typeof currentProject.owner_id === 'string' &&
    currentProject.owner_id.trim().length > 0
      ? currentProject.owner_id
      : null;
  const primaryMemberOrganizationId = useMemo(() => {
    const primaryOrganizations = memberOrganizations.filter(
      (organization) => organization.membership?.isPrimary === true,
    );

    if (primaryOrganizations.length !== 1) {
      return null;
    }

    return getTrimmedString(primaryOrganizations[0]?._id);
  }, [memberOrganizations]);
  const editorContextOrganizationId =
    projectOrganizationId ?? getTrimmedString(viewerOrganizationId);
  const effectiveRequestingOrganizationId =
    routeOrganizationSlug !== null
      ? routeOrganizationId ?? resolvedRouteOrganizationId
      : requestingOrganizationId ?? editorContextOrganizationId ?? primaryMemberOrganizationId;
  const manualSchemaOptions = getManualSchemaOptions(discoveryResult);
  const manualSelectedSchema = manualSchemaOptions.find(
    (schema) => schema.id === manualSingleSourceDraft.source?.schemaId,
  );
  const manualDraftFieldOptions = [
    manualSingleSourceDraft.timeRange?.field,
    manualSingleSourceDraft.groupBy?.[0]?.field,
    ...manualSingleSourceDraft.measures.map((measure) => measure.field),
  ].filter((field): field is string => typeof field === 'string' && field.trim().length > 0);
  const manualFieldOptions = Array.from(new Set([...getSchemaFieldOptions(manualSelectedSchema), ...manualDraftFieldOptions]));

  // Voice control for Create tab
  const { voiceState, error: voiceError, handleMicPress, handleStopSpeaking, setOnTranscription } = useVoiceControl();

  // Wire voice transcription to the create input
  useEffect(() => {
    setOnTranscription((text: string) => {
      setCreateInput((prev) => (prev ? prev + ' ' + text : text));
    });
  }, [setOnTranscription]);

  // Reset state on close
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('templates');
      setCreateMode('ai');
      setCreateInput('');
      setCreateLoading(false);
      setCreateResult(null);
      setCreateReviewAccepted(false);
      setCreateClarificationSelectedValue(null);
      setCreateClarificationFreeText('');
      setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
      setManualImportedDraft(null);
      setManualImportedCompositeDefinition(null);
      setManualImportedSaveMetadata(undefined);
      setManualImportNotice(null);
      setManualImportVersion(0);
      setCreateSaveForm(emptyCreateSaveForm());
      setCreateError(null);
      setIsSaving(false);
    }
  }, [isOpen]);

  const handleImportedCsvDataset = useCallback((result: CsvDatasetImportResult) => {
    handleImportedCsvDatasetResult(result);
    toast.success(
      `Imported ${result.insertedCount} row${result.insertedCount === 1 ? '' : 's'} successfully.`,
    );
    setActiveTab('schemas');
  }, [handleImportedCsvDatasetResult]);
  const handleTabChange = useCallback((value: string) => {
    if (isActiveTab(value)) {
      setActiveTab(value);
    }
  }, []);
  // --- Create tab handlers ---
  const generateCreateResultFromPrompt = useCallback(
    async (
      prompt: string,
      sourceHint?: string,
      clarificationResponse?: ClarificationResponsePayload,
      requestPrompt = prompt,
    ) => {
      return generateCreateTemplateResult(
        prompt,
        {
          generateSingleSource: () =>
            dataApi.query(requestPrompt, sourceHint, true, undefined, {
              responseMode: 'template-create',
              ...(clarificationResponse && { clarificationResponse }),
            }),
          generateComposite: () => dataApi.generateCompositeQuery(requestPrompt, clarificationResponse),
          previewComposite: previewCompositeRows,
        },
        {
          clarificationDepth: clarificationResponse?.depth ?? 0,
          maxClarificationDepth: 1,
        },
      );
    },
    [],
  );

  const handleGenerate = useCallback(async () => {
    if (!createInput.trim() || createLoading) return;
    const trimmedInput = createInput.trim();

    setCreateLoading(true);
    setCreateError(null);
    setCreateResult(null);
    setCreateReviewAccepted(false);
    setCreateClarificationSelectedValue(null);
    setCreateClarificationFreeText('');
    setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
    setManualImportedDraft(null);
    setManualImportedCompositeDefinition(null);
    setManualImportedSaveMetadata(undefined);
    setManualImportNotice(null);
    setManualImportVersion(0);
    try {
      const generated = await generateCreateResultFromPrompt(trimmedInput);

      setCreateResult(generated.result);
      setCreateSaveForm(
        generated.result
          ? buildTemplateSaveMetadataFromCreateResult(generated.result, trimmedInput)
          : emptyCreateSaveForm(),
      );
      setCreateError(generated.error);
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to generate query');
    } finally {
      setCreateLoading(false);
    }
  }, [createInput, createLoading, generateCreateResultFromPrompt]);

  const handleResolveClarification = useCallback(
    async (answer: { value: string; label?: string; optionId?: string; isFreeText?: boolean; sourceHint?: string }) => {
      if (!createResult || createResult.kind !== 'clarification' || createLoading) return;

      const trimmedAnswer = answer.value.trim();
      if (!trimmedAnswer) return;

      setCreateLoading(true);
      setCreateError(null);
      try {
        const resumePrompt = buildCreateClarificationResumePrompt(createResult.clarification, {
          value: trimmedAnswer,
          label: answer.label,
          optionId: answer.optionId,
          isFreeText: answer.isFreeText,
        });
        const clarificationResponse = buildCreateClarificationResponsePayload(createResult.clarification, {
          value: trimmedAnswer,
          label: answer.label,
          optionId: answer.optionId,
          isFreeText: answer.isFreeText,
        });
        const generated = await generateCreateResultFromPrompt(
          resumePrompt,
          answer.sourceHint,
          clarificationResponse,
          createResult.clarification.originalPrompt,
        );

        setCreateResult(generated.result);
        setCreateReviewAccepted(false);
        setCreateError(generated.error);
        setCreateClarificationSelectedValue(null);
        setCreateClarificationFreeText('');
        setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
        setManualImportedDraft(null);
        setManualImportedCompositeDefinition(null);
        setManualImportedSaveMetadata(undefined);
        setManualImportNotice(null);
        setManualImportVersion(0);
        setCreateSaveForm(
          generated.result
            ? buildTemplateSaveMetadataFromCreateResult(generated.result, createResult.clarification.originalPrompt)
            : emptyCreateSaveForm(),
        );
      } catch (err) {
        setCreateError(err instanceof Error ? err.message : 'Failed to resolve clarification');
      } finally {
        setCreateLoading(false);
      }
    },
    [createLoading, createResult, generateCreateResultFromPrompt],
  );

  const saveCreateTemplateResult = useCallback(async (
    result: CreateTemplateResult,
    options: GeneratedCreateActionOptions,
    afterSave: () => void,
  ) => {
    if (!createSaveForm.name.trim() || isSaving) {
      return;
    }

    setIsSaving(true);
    try {
      const commonFields = {
        name: createSaveForm.name.trim(),
        description: createSaveForm.description.trim() || undefined,
        tags: createSaveForm.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
        folder: createSaveForm.folder.trim() || undefined,
      };

      const savePayload = buildGeneratedCreateSavePayload(result, options);
      if (!savePayload.ok) {
        toast.error(savePayload.disabledReason);
        return;
      }

      await queryTemplateApi.create({
        ...commonFields,
        ...savePayload.payload,
      });

      toast.success('Template saved successfully');
      afterSave();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setIsSaving(false);
    }
  }, [createSaveForm, isSaving]);

  const handleSaveTemplate = useCallback(async () => {
    if (
      !createResult ||
      !createSaveForm.name.trim() ||
      createResult.metadata.needsClarification ||
      (createResult.metadata.needsReview && (!canAcceptCreateReview(createResult) || !createReviewAccepted))
    ) {
      return;
    }

    await saveCreateTemplateResult(
      createResult,
      {
        reviewAccepted: createReviewAccepted,
        isPersonalSpace,
        discoveryResult,
      },
      () => {
        setCreateInput('');
        setCreateResult(null);
        setCreateReviewAccepted(false);
        setCreateClarificationSelectedValue(null);
        setCreateClarificationFreeText('');
        setCreateSaveForm(emptyCreateSaveForm());
      },
    );
  }, [createResult, createReviewAccepted, createSaveForm.name, discoveryResult, isPersonalSpace, saveCreateTemplateResult]);

  const getManualActionOptions = useCallback((): GeneratedCreateActionOptions => ({
    reviewAccepted: true,
    isPersonalSpace,
    discoveryResult,
    sourceOrganizationId: manualSingleSourceDraft.source?.organizationId,
    sourceOrganizationName: manualSingleSourceDraft.source?.organizationName,
  }), [discoveryResult, isPersonalSpace, manualSingleSourceDraft.source]);

  const handleManualSourceChange = useCallback((schemaId: string) => {
    const schema = manualSchemaOptions.find((candidate) => candidate.id === schemaId);
    if (!schema) {
      setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
      return;
    }

    const fields = getSchemaFieldOptions(schema);
    const timeField = inferInitialTimeField(fields);
    const measureField = inferInitialMeasureField(fields);

    setManualSingleSourceDraft((prev) => {
      const aggregation = prev.measures[0]?.aggregation ?? 'sum';
      return updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        source: {
          schemaId: schema.id,
          groupId: schema.groupId,
          schemaName: getSchemaLabel(schema),
          sourceId: schema.id,
          sourceName: getSchemaLabel(schema),
          organizationId: schema.organizationId,
          organizationName: schema.organizationName,
        },
        measures: [
          {
            field: measureField,
            aggregation,
            alias: buildManualAlias(aggregation, measureField),
          },
        ],
        timeRange: timeField
          ? {
              field: timeField,
              preset: draft.timeRange?.preset ?? 'last_24h',
            }
          : {
              preset: draft.timeRange?.preset ?? 'last_24h',
            },
        groupBy: timeField ? [{ field: timeField, granularity: 'hour' }] : undefined,
        limit: draft.limit ?? 100,
      }));
    });
  }, [manualSchemaOptions]);

  const handleManualMeasureFieldChange = useCallback((field: string) => {
    setManualSingleSourceDraft((prev) => {
      const aggregation = prev.measures[0]?.aggregation ?? 'sum';
      return updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        measures: [
          {
            ...draft.measures[0],
            field: field || undefined,
            aggregation,
            alias: buildManualAlias(aggregation, field || undefined),
          },
        ],
      }));
    });
  }, []);

  const handleManualAggregationChange = useCallback((aggregation: string) => {
    setManualSingleSourceDraft((prev) => {
      const field = prev.measures[0]?.field;
      return updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        measures: [
          {
            ...draft.measures[0],
            field,
            aggregation,
            alias: buildManualAlias(aggregation, field),
          },
        ],
      }));
    });
  }, []);

  const handleManualAliasChange = useCallback((alias: string) => {
    setManualSingleSourceDraft((prev) =>
      updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        measures: [
          {
            ...draft.measures[0],
            alias,
          },
        ],
      })),
    );
  }, []);

  const handleManualGroupByFieldChange = useCallback((field: string) => {
    setManualSingleSourceDraft((prev) =>
      updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        groupBy: field
          ? [
              {
                field,
                granularity: draft.groupBy?.[0]?.granularity,
              },
            ]
          : undefined,
      })),
    );
  }, []);

  const handleManualGranularityChange = useCallback((granularity: string) => {
    setManualSingleSourceDraft((prev) => {
      const field = prev.groupBy?.[0]?.field ?? prev.timeRange?.field;
      return updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        groupBy: field
          ? [
              {
                field,
                granularity: granularity || undefined,
              },
            ]
          : undefined,
      }));
    });
  }, []);

  const handleManualTimeFieldChange = useCallback((field: string) => {
    setManualSingleSourceDraft((prev) =>
      updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        timeRange: {
          ...(draft.timeRange ?? {}),
          field: field || undefined,
        },
      })),
    );
  }, []);

  const handleManualTimePresetChange = useCallback((preset: string) => {
    setManualSingleSourceDraft((prev) =>
      updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        timeRange: {
          ...(draft.timeRange ?? {}),
          preset: preset || undefined,
        },
      })),
    );
  }, []);

  const handleManualLimitChange = useCallback((value: string) => {
    const numericValue = Number(value);
    setManualSingleSourceDraft((prev) =>
      updateManualSingleSourceDraftClearingPreview(prev, (draft) => ({
        ...draft,
        limit: value && Number.isFinite(numericValue) ? Math.max(1, Math.floor(numericValue)) : undefined,
      })),
    );
  }, []);

  const handleSaveManualTemplate = useCallback(async () => {
    const conversion = buildManualCreateTemplateResult(manualSingleSourceDraft);
    if (!conversion.ok) {
      toast.error(conversion.disabledReason);
      return;
    }

    await saveCreateTemplateResult(conversion.result, getManualActionOptions(), () => {
      setCreateInput('');
      setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
      setCreateSaveForm(emptyCreateSaveForm());
    });
  }, [getManualActionOptions, manualSingleSourceDraft, saveCreateTemplateResult]);

  const handleApplyManualTemplate = useCallback(() => {
    if (!component) {
      return;
    }

    const conversion = buildManualCreateTemplateResult(manualSingleSourceDraft);
    if (!conversion.ok) {
      toast.error(conversion.disabledReason);
      return;
    }

    const applyPayload = buildGeneratedCreateApplyPayload(conversion.result, getManualActionOptions());
    if (!applyPayload.ok) {
      toast.error(applyPayload.disabledReason);
      return;
    }

    if (applyPayload.payload.type === 'single-source') {
      handleApplyQueryDefinition({
        queryDefinition: applyPayload.payload.queryDefinition,
        organizationId: applyPayload.payload.organizationId,
        organizationName: applyPayload.payload.organizationName,
      });
    }
  }, [component, getManualActionOptions, handleApplyQueryDefinition, manualSingleSourceDraft]);

  const handleResetCreate = useCallback(() => {
    setCreateInput('');
    setCreateResult(null);
    setCreateReviewAccepted(false);
    setCreateClarificationSelectedValue(null);
    setCreateClarificationFreeText('');
    setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
    setManualImportedDraft(null);
    setManualImportedCompositeDefinition(null);
    setManualImportedSaveMetadata(undefined);
    setManualImportNotice(null);
    setManualImportVersion(0);
    setCreateSaveForm(emptyCreateSaveForm());
    setCreateError(null);
  }, []);

  const importGeneratedResultToManualDraft = useCallback((
    result: CreateTemplateResult,
    options: GeneratedCreateActionOptions,
  ): { ok: true } | { ok: false; disabledReason: string } => {
    const importedDraft = buildManualDraftFromGeneratedCreateResult(result, options);
    if (!importedDraft.ok) {
      return importedDraft;
    }

    if (importedDraft.draft.kind === 'single-source') {
      setManualSingleSourceDraft(importedDraft.draft);
      setManualImportedDraft(null);
      setManualImportedCompositeDefinition(null);
      setManualImportedSaveMetadata(undefined);
    } else {
      setManualImportedDraft(importedDraft.draft);
      setManualImportedCompositeDefinition(result.kind === 'composite' ? result.definition : null);
      setManualImportedSaveMetadata(snapshotTemplateSaveMetadata(createSaveForm));
    }

    setManualImportVersion((version) => version + 1);
    setManualImportNotice(importedDraft.warning ?? null);
    return { ok: true };
  }, [createSaveForm]);

  const handleOpenGeneratedManualBuilder = useCallback((
    result: CreateTemplateResult,
    options: GeneratedCreateActionOptions,
  ) => {
    const importedDraft = importGeneratedResultToManualDraft(result, options);
    if (!importedDraft.ok) {
      setCreateError(importedDraft.disabledReason);
      toast.error(importedDraft.disabledReason);
      return;
    }

    setCreateError(null);
    setCreateMode('manual');
  }, [importGeneratedResultToManualDraft]);

  const handleOpenManualBuilderMode = useCallback(() => {
    if (!createResult) {
      setManualImportNotice(null);
      setCreateMode('manual');
      return;
    }

    const importedDraft = importGeneratedResultToManualDraft(createResult, {
      reviewAccepted: createReviewAccepted,
      isPersonalSpace,
      discoveryResult,
    });

    if (!importedDraft.ok) {
      setCreateError(importedDraft.disabledReason);
      toast.error(importedDraft.disabledReason);
      return;
    }

    setCreateError(null);
    setCreateMode('manual');
  }, [createResult, createReviewAccepted, discoveryResult, importGeneratedResultToManualDraft, isPersonalSpace]);

  const manualConversion = buildManualCreateTemplateResult(manualSingleSourceDraft);
  const manualActionOptions = getManualActionOptions();
  const manualActionState = manualConversion.ok
    ? getGeneratedCreateActionState(manualConversion.result, manualActionOptions)
    : null;
  const manualApplyPayload = manualConversion.ok
    ? buildGeneratedCreateApplyPayload(manualConversion.result, manualActionOptions)
    : null;
  const manualPreviewModel =
    manualConversion.ok && manualConversion.result.kind === 'template'
      ? buildCreateTemplatePreviewModel(manualConversion.result)
      : null;
  const manualDisabledReason =
    manualConversion.ok
      ? manualActionState?.apply ?? manualActionState?.save ?? null
      : manualConversion.disabledReason;
  const manualApplyDisabledReason =
    manualConversion.ok
      ? manualActionState?.apply ?? (manualApplyPayload?.ok === false ? manualApplyPayload.disabledReason : null)
      : manualConversion.disabledReason;
  const manualSaveDisabledReason = manualConversion.ok ? manualActionState?.save ?? null : manualConversion.disabledReason;
  const manualHasSelectedComponent = Boolean(component);
  const manualCompositeDefinition = manualImportedCompositeDefinition ?? getManualCompositeDefinition(manualImportedDraft);
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent variant="light" className="sm:max-w-4xl p-0 gap-0">
        <DialogTitle className="sr-only">Data templates and schema discovery</DialogTitle>
        <DialogDescription className="sr-only">
          Browse templates and schemas, configure workflow data sources, and create reusable data templates.
        </DialogDescription>
        {/* Initial loading state */}
        {isLoading && !discoveryResult && !schemaExplorationResult && (
          <div className="flex flex-col items-center justify-center py-16 px-6 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">Discovering data sources...</p>
          </div>
        )}

        {/* Error on initial load */}
        {error && !discoveryResult && (
          <div className="px-6 py-10 flex flex-col items-center gap-4">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <p className="text-sm">{error}</p>
            </div>
            <button onClick={() => openModal()} className="text-sm text-emerald-600 hover:text-emerald-700 dark:hover:text-emerald-400 font-medium">
              Retry
            </button>
          </div>
        )}

        {/* Tabbed content (shown once browse data loaded or on non-browse tabs) */}
        {(discoveryResult || (!isLoading && !error)) && (
          <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col">
            <div className="border-b border-gray-200 dark:border-gray-700 pl-4 pr-12 pt-4 pb-3 bg-muted">
              <TabsList className="grid h-9 w-full grid-cols-5">
                <TabsTrigger value="templates" className="flex items-center gap-1.5 text-xs">
                  <FileText className="h-3.5 w-3.5" />
                  Templates
                </TabsTrigger>
                <TabsTrigger value="schemas" className="flex items-center gap-1.5 text-xs">
                  <Database className="h-3.5 w-3.5" />
                  Schemas
                </TabsTrigger>
                <TabsTrigger value="workflow" className="flex items-center gap-1.5 text-xs">
                  <GitBranch className="h-3.5 w-3.5" />
                  Workflow
                </TabsTrigger>
                <TabsTrigger value="import" className="flex items-center gap-1.5 text-xs">
                  <Upload className="h-3.5 w-3.5" />
                  Import CSV
                </TabsTrigger>
                <TabsTrigger value="create" className="flex items-center gap-1.5 text-xs">
                  <Sparkles className="h-3.5 w-3.5" />
                  Create
                </TabsTrigger>
              </TabsList>
            </div>

            {/* ============================================================= */}
            {/* Schemas Tab                                                    */}
            {/* ============================================================= */}
            <TabsContent value="schemas" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
              {discoveryResult && !schemaExplorationResult && (
                <DataDiscoveryCard discoveryResult={discoveryResult} onSelectSchema={handleSelectSchema} onExploreCategory={() => {}} />
              )}

              {schemaExplorationResult && (
                <>
                  {isLoading && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />
                    </div>
                  )}
                  <SchemaDetailCard
                    explorationResult={schemaExplorationResult}
                    onUseSchema={handleUseSchema}
                    onSampleQuery={handleSuggestedQuery}
                    onBack={handleBackToDiscovery}
                    hasSelectedChart={Boolean(component?.type?.includes('chart') || component?.type?.includes('gauge'))}
                  />
                </>
              )}

              {!discoveryResult && !schemaExplorationResult && !isLoading && (
                <div className="flex flex-col items-center justify-center py-16 px-6 gap-3">
                  <Database className="h-8 w-8 text-gray-300 dark:text-gray-600" />
                  <p className="text-sm text-gray-400 dark:text-gray-500">No data sources found</p>
                </div>
              )}
            </TabsContent>

            {/* ============================================================= */}
            {/* Import Tab                                                     */}
            {/* ============================================================= */}
            <TabsContent value="import" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
              <div className="border-b border-gray-200 bg-gray-50/70 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/20">
                <div className="mb-3">
                  <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    Import CSV data
                  </h3>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Upload a CSV file, create a schema-backed source, and make it available in this modal for templates and bindings.
                  </p>
                </div>
                <CsvDatasetImportPanel
                  open={isOpen && activeTab === 'import'}
                  requestingOrganizationId={effectiveRequestingOrganizationId}
                  onImported={handleImportedCsvDataset}
                />
              </div>
            </TabsContent>

            {/* ============================================================= */}
            {/* Templates Tab                                                  */}
            {/* ============================================================= */}
            <TabsContent value="templates" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
              <TemplateManagementTab onSelectTemplate={handleSelectTemplate} onSelectTemplateField={handleSelectTemplateField} component={component ?? null} />
            </TabsContent>

            {/* ============================================================= */}
            {/* Workflow Tab                                                    */}
            {/* ============================================================= */}
            <TabsContent value="workflow" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 overflow-y-auto mt-0">
              <WorkflowDataSourceTab onSelectWorkflow={handleSelectWorkflow} />
            </TabsContent>

            {/* ============================================================= */}
            {/* Create Tab (AI Template Builder)                               */}
            {/* ============================================================= */}
            <TabsContent value="create" className="data-[state=active]:h-[70vh] data-[state=inactive]:h-0 min-h-0 flex flex-col overflow-hidden mt-0">
              <div className="shrink-0 px-4 pb-2 pt-3">
                <div className="inline-flex max-w-full flex-wrap items-center gap-1 rounded-lg border border-border/60 bg-muted/30 p-1">
                  <button
                    type="button"
                    aria-pressed={createMode === 'ai'}
                    onClick={() => setCreateMode('ai')}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      createMode === 'ai'
                        ? 'border-border bg-background text-foreground shadow-sm'
                        : 'border-transparent text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    }`}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    AI Generate
                  </button>
                  <button
                    type="button"
                    aria-pressed={createMode === 'manual'}
                    onClick={handleOpenManualBuilderMode}
                    className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${
                      createMode === 'manual'
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-900 shadow-sm dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100'
                        : 'border-transparent text-muted-foreground hover:bg-background/70 hover:text-foreground'
                    }`}
                  >
                    <Settings2 className="h-3.5 w-3.5" />
                    Manual Builder
                    <span
                      className={`rounded-full px-1.5 py-0 text-[10px] ${
                        createMode === 'manual'
                          ? 'border border-emerald-300 bg-white/70 text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200'
                          : 'border border-slate-300 text-slate-500 dark:border-slate-600 dark:text-slate-400'
                      }`}
                    >
                      Advanced
                    </span>
                  </button>
                </div>
              </div>

              {createMode === 'manual' && manualImportedDraft && manualImportedDraft.kind !== 'single-source' ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden pt-3">
                  <div
                    className="mx-4 mb-3 shrink-0 rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-3 dark:border-emerald-800 dark:bg-emerald-900/20"
                    data-testid="manual-builder-imported-draft"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                            Manual Builder
                          </h3>
                          <span className="rounded-full border border-emerald-300 bg-white/70 px-2 py-0.5 text-[10px] font-medium uppercase text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                            Advanced
                          </span>
                        </div>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Review or fine-tune the generated definition.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setManualImportedDraft(null);
                          setManualImportedCompositeDefinition(null);
                          setManualImportedSaveMetadata(undefined);
                          setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
                          setCreateSaveForm(emptyCreateSaveForm());
                        }}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-700/50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Start Empty Draft
                      </button>
                    </div>
                  </div>
                  <div className="min-h-0 flex-1">
                    <CompositeQueryBuilderTab
                      key={`manual-builder-${manualImportVersion}`}
                      component={component}
                      discoveryResult={discoveryResult}
                      onApply={handleSelectComposite}
                      hasApplyTarget={manualHasSelectedComponent}
                      initialDefinition={manualCompositeDefinition}
                      initialSaveMetadata={manualImportedSaveMetadata}
                      initialJoinWarning={manualImportNotice}
                      initialMode="visual"
                      showModeToggle={false}
                    />
                  </div>
                </div>
              ) : createMode === 'manual' ? (
                <div className="min-h-0 flex-1 overflow-y-auto scroll-pt-4 p-4 space-y-4" data-testid="manual-single-source-builder">
                  <div className="rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-3 dark:border-emerald-800 dark:bg-emerald-900/20">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Manual Builder</h3>
                      <span className="rounded-full border border-emerald-300 bg-white/70 px-2 py-0.5 text-[10px] font-medium uppercase text-emerald-700 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200">
                        Advanced
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {manualSingleSourceDraft.source
                        ? 'Review or fine-tune the generated definition.'
                        : 'Choose an exact source and fields to build a template manually.'}
                    </p>
                    {manualImportNotice && (
                      <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
                        {manualImportNotice}
                      </p>
                    )}
                  </div>

                  <div className="rounded-md border border-gray-200 bg-background p-3 dark:border-gray-700">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Source/schema <span className="text-red-500 dark:text-red-400">*</span>
                        </span>
                        <select
                          aria-label="Manual source/schema"
                          value={manualSingleSourceDraft.source?.schemaId ?? ''}
                          onChange={(event) => handleManualSourceChange(event.target.value)}
                          className={manualSelectClass}
                        >
                          <option value="">Choose a source</option>
                          {manualSingleSourceDraft.source?.schemaId &&
                            !manualSchemaOptions.some((schema) => schema.id === manualSingleSourceDraft.source?.schemaId) && (
                              <option value={manualSingleSourceDraft.source.schemaId}>
                                {manualSingleSourceDraft.source.schemaName ??
                                  manualSingleSourceDraft.source.sourceName ??
                                  manualSingleSourceDraft.source.schemaId}
                              </option>
                            )}
                          {manualSchemaOptions.map((schema) => (
                            <option key={schema.id} value={schema.id}>
                              {getSchemaLabel(schema)}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          Measure field <span className="text-red-500 dark:text-red-400">*</span>
                        </span>
                        <select
                          aria-label="Manual measure field"
                          value={manualSingleSourceDraft.measures[0]?.field ?? ''}
                          onChange={(event) => handleManualMeasureFieldChange(event.target.value)}
                          className={manualSelectClass}
                          disabled={!manualSingleSourceDraft.source}
                        >
                          <option value="">Choose a field</option>
                          {manualFieldOptions.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Aggregation</span>
                        <select
                          aria-label="Manual aggregation"
                          value={manualSingleSourceDraft.measures[0]?.aggregation ?? 'sum'}
                          onChange={(event) => handleManualAggregationChange(event.target.value)}
                          className={manualSelectClass}
                        >
                          {manualAggregationOptions.map((aggregation) => (
                            <option key={aggregation} value={aggregation}>
                              {aggregation}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Output alias</span>
                        <input
                          aria-label="Manual output alias"
                          type="text"
                          value={manualSingleSourceDraft.measures[0]?.alias ?? ''}
                          onChange={(event) => handleManualAliasChange(event.target.value)}
                          placeholder="e.g., totalGallons"
                          className={createSaveInputClass}
                        />
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Group-by field</span>
                        <select
                          aria-label="Manual group-by field"
                          value={manualSingleSourceDraft.groupBy?.[0]?.field ?? ''}
                          onChange={(event) => handleManualGroupByFieldChange(event.target.value)}
                          className={manualSelectClass}
                          disabled={!manualSingleSourceDraft.source}
                        >
                          <option value="">No group-by</option>
                          {manualFieldOptions.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Time granularity</span>
                        <select
                          aria-label="Manual time granularity"
                          value={manualSingleSourceDraft.groupBy?.[0]?.granularity ?? ''}
                          onChange={(event) => handleManualGranularityChange(event.target.value)}
                          className={manualSelectClass}
                          disabled={!manualSingleSourceDraft.groupBy?.[0]?.field}
                        >
                          <option value="">None</option>
                          {manualTimeGranularityOptions.map((granularity) => (
                            <option key={granularity} value={granularity}>
                              {granularity}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Time field</span>
                        <select
                          aria-label="Manual time field"
                          value={manualSingleSourceDraft.timeRange?.field ?? ''}
                          onChange={(event) => handleManualTimeFieldChange(event.target.value)}
                          className={manualSelectClass}
                          disabled={!manualSingleSourceDraft.source}
                        >
                          <option value="">No time field</option>
                          {manualFieldOptions.map((field) => (
                            <option key={field} value={field}>
                              {field}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Time range preset</span>
                        <select
                          aria-label="Manual time range preset"
                          value={manualSingleSourceDraft.timeRange?.preset ?? ''}
                          onChange={(event) => handleManualTimePresetChange(event.target.value)}
                          className={manualSelectClass}
                        >
                          <option value="">No preset</option>
                          {manualTimeRangePresetOptions.map((preset) => (
                            <option key={preset} value={preset}>
                              {preset}
                            </option>
                          ))}
                        </select>
                      </label>

                      <label className="space-y-1">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Limit</span>
                        <input
                          aria-label="Manual limit"
                          type="number"
                          min={1}
                          value={manualSingleSourceDraft.limit ?? ''}
                          onChange={(event) => handleManualLimitChange(event.target.value)}
                          className={createSaveInputClass}
                        />
                      </label>
                    </div>
                  </div>

                  {manualPreviewModel ? (
                    <div className="border border-emerald-200 dark:border-emerald-800 rounded-md bg-emerald-50/50 dark:bg-emerald-900/20">
                      <CreateResultPreviewCard previewModel={manualPreviewModel} needsReview={false} />

                      <div className="border-t border-emerald-200 dark:border-emerald-800 px-3 py-3 space-y-3">
                        <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                          <Save className="h-3.5 w-3.5" />
                          Save as Template
                        </h4>
                        <TemplateSaveMetadataFields
                          idPrefix="manual-template"
                          value={createSaveForm}
                          onFieldChange={handleCreateSaveMetadataFieldChange}
                        />

                        {manualDisabledReason && (
                          <p className="text-xs text-amber-700 dark:text-amber-300">{manualDisabledReason}</p>
                        )}

                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          {manualHasSelectedComponent && (
                            <button
                              type="button"
                              onClick={handleApplyManualTemplate}
                              disabled={!manualApplyPayload?.ok}
                              title={manualApplyPayload?.ok ? undefined : manualApplyDisabledReason ?? undefined}
                              className={getCreateActionButtonClass(true)}
                            >
                              <ChevronRight className="h-3.5 w-3.5" />
                              Apply to Chart
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleSaveManualTemplate}
                            disabled={!createSaveForm.name.trim() || isSaving || !manualActionState?.canSave}
                            title={manualActionState?.canSave ? undefined : manualSaveDisabledReason ?? undefined}
                            className={getCreateActionButtonClass(!manualHasSelectedComponent)}
                          >
                            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                            Save Template
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setManualSingleSourceDraft(createEmptyManualSingleSourceDraft());
                              setCreateSaveForm(emptyCreateSaveForm());
                            }}
                            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700/50"
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-3 dark:border-amber-800 dark:bg-amber-900/20">
                      <p className="text-sm font-medium text-amber-900 dark:text-amber-100">Manual draft is incomplete.</p>
                      <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">{manualDisabledReason}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {manualHasSelectedComponent && (
                          <button
                            type="button"
                            disabled
                            title={manualApplyDisabledReason ?? undefined}
                            className={getCreateActionButtonClass(true)}
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                            Apply to Chart
                          </button>
                        )}
                        <button
                          type="button"
                          disabled
                          title={manualSaveDisabledReason ?? undefined}
                          className={getCreateActionButtonClass(!manualHasSelectedComponent)}
                        >
                          <Save className="h-3.5 w-3.5" />
                          Save Template
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="min-h-0 flex-1 flex flex-col overflow-hidden">
                  {/* Scrollable content area */}
                  <div className="min-h-0 flex-1 overflow-y-auto scroll-pt-4 p-4 space-y-4">
                    {/* Error */}
                    {createError && (
                      <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0" />
                        <span>{createError}</span>
                      </div>
                    )}

                    {/* Loading */}
                    {createLoading && (
                      <CreateResultPreviewSkeleton />
                    )}

                    {/* Result Preview */}
                    {createResult && !createLoading && (
                      <div className="space-y-4">
                        {(() => {
                          if (createResult.kind === 'clarification') {
                            const resultTypeLabel = getDisplayResultTypeLabel(createResult.metadata);
                            const sourceOptions = createResult.clarification.options;
                            const freeTextAnswer = createClarificationFreeText.trim();

                            return (
                              <div
                                className="border border-amber-200 dark:border-amber-800 rounded-md bg-amber-50/60 dark:bg-amber-900/20"
                                data-testid="create-clarification-result"
                              >
                                <div className="px-3 py-2 border-b border-amber-200 dark:border-amber-800">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <h4 className="text-xs font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-1.5">
                                      <ChevronRight className="h-3.5 w-3.5" />
                                      Result type: {resultTypeLabel}
                                    </h4>
                                    <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                      Needs Clarification
                                    </span>
                                  </div>
                                </div>
                                <div className="px-3 py-3 space-y-3">
                                  <div className="space-y-1">
                                    <p className="text-sm font-semibold leading-5 text-amber-950 dark:text-amber-100">
                                      {createResult.clarification.question}
                                    </p>
                                  </div>
                                  {createResult.metadata.clarificationReason && (
                                    <p className="text-xs text-amber-700 dark:text-amber-300">{createResult.metadata.clarificationReason}</p>
                                  )}
                                  {sourceOptions.length > 0 && (
                                    <div className="space-y-1.5" role="group" aria-label="Clarification options">
                                      {sourceOptions.map((option) => (
                                        <button
                                          type="button"
                                          key={option.id}
                                          onClick={() => {
                                            setCreateClarificationSelectedValue(option.value);
                                            handleResolveClarification({
                                              value: option.value,
                                              label: option.label,
                                              optionId: option.id,
                                              isFreeText: false,
                                              sourceHint: getCreateClarificationSourceHint(createResult.clarification, option),
                                            });
                                          }}
                                          disabled={createLoading}
                                          aria-pressed={createClarificationSelectedValue === option.value}
                                          className={`flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left text-xs transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                                            createClarificationSelectedValue === option.value
                                              ? 'border-amber-500 bg-white text-amber-950 shadow-sm dark:bg-gray-900 dark:text-amber-100'
                                              : 'border-amber-200 bg-white/80 text-gray-700 hover:border-amber-400 hover:bg-white dark:border-amber-800 dark:bg-gray-900/80 dark:text-gray-300 dark:hover:border-amber-600'
                                          }`}
                                          title={option.reasoning ?? option.description}
                                        >
                                          <span>
                                            <span className="block font-medium">{option.label}</span>
                                            {(option.description || option.reasoning) && (
                                              <span className="mt-0.5 block text-[11px] leading-4 text-gray-500 dark:text-gray-400">
                                                {option.description ?? option.reasoning}
                                              </span>
                                            )}
                                          </span>
                                          {typeof option.confidence === 'number' && (
                                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                              {Math.round(option.confidence * 100)}%
                                            </span>
                                          )}
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {createResult.clarification.allowsFreeText && (
                                    <div className="space-y-1.5">
                                      <label
                                        htmlFor="create-clarification-free-text"
                                        className="block text-xs font-medium text-amber-900 dark:text-amber-200"
                                      >
                                        Custom answer
                                      </label>
                                      <div className="flex gap-2">
                                        <input
                                          id="create-clarification-free-text"
                                          type="text"
                                          value={createClarificationFreeText}
                                          onChange={(e) => setCreateClarificationFreeText(e.target.value)}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              e.preventDefault();
                                              handleResolveClarification({
                                                value: freeTextAnswer,
                                                isFreeText: true,
                                                sourceHint: createResult.clarification.resumeContext.sourceHint,
                                              });
                                            }
                                          }}
                                          disabled={createLoading}
                                          placeholder="Type an answer"
                                          className={createSaveInputClass}
                                        />
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleResolveClarification({
                                              value: freeTextAnswer,
                                              isFreeText: true,
                                              sourceHint: createResult.clarification.resumeContext.sourceHint,
                                            })
                                          }
                                          disabled={!freeTextAnswer || createLoading}
                                          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                          {createLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowUp className="h-3.5 w-3.5" />}
                                          Use
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                  <p className="text-xs text-amber-700 dark:text-amber-300">{createResult.metadata.saveDisabledReason}</p>
                                  <div className="flex items-center gap-2 pt-1">
                                    <button
                                      type="button"
                                      disabled
                                      title={createResult.metadata.saveDisabledReason}
                                      className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white opacity-50 cursor-not-allowed"
                                    >
                                      <Save className="h-3.5 w-3.5" />
                                      Save Template
                                    </button>
                                    <button
                                      type="button"
                                      onClick={handleResetCreate}
                                      className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                      <RotateCcw className="h-3.5 w-3.5" />
                                      Reset
                                    </button>
                                  </div>
                                </div>
                              </div>
                            );
                          }

                          if (createResult.kind === 'composite') {
                            const previewModel = buildCreateCompositePreviewModel(createResult);
                            const reviewCanBeAccepted = canAcceptCreateReview(createResult);
                            const currentActionOptions: GeneratedCreateActionOptions = {
                              reviewAccepted: createReviewAccepted,
                              isPersonalSpace,
                              discoveryResult,
                            };
                            const futureActionOptions: GeneratedCreateActionOptions = {
                              reviewAccepted: true,
                              isPersonalSpace,
                              discoveryResult,
                            };
                            const currentGeneratedActionState = getGeneratedCreateActionState(createResult, currentActionOptions);
                            const futureGeneratedActionState = getGeneratedCreateActionState(createResult, futureActionOptions);
                            const currentApplyPayload = buildGeneratedCreateApplyPayload(createResult, currentActionOptions);
                            const futureApplyPayload = buildGeneratedCreateApplyPayload(createResult, futureActionOptions);
                            const saveDisabledReason = currentGeneratedActionState.save;
                            const showManualEditButton = currentGeneratedActionState.canSave;
                            const showApplyButton =
                              Boolean(component) &&
                              futureGeneratedActionState.canApply &&
                              futureApplyPayload.ok &&
                              futureApplyPayload.payload.type === 'composite';
                            const saveIsPrimary = !showApplyButton;

                            return (
                              <>
                                {createResult.explanation && (
                                  <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md p-3">
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{createResult.explanation}</p>
                                  </div>
                                )}

                                <div className="border border-emerald-200 dark:border-emerald-800 rounded-md bg-emerald-50/50 dark:bg-emerald-900/20">
                                  <CreateResultPreviewCard
                                    previewModel={previewModel}
                                    needsReview={createResult.metadata.needsReview}
                                  />

                                  <div className="border-t border-emerald-200 dark:border-emerald-800 px-3 py-3 space-y-3">
                                    <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                      <Save className="h-3.5 w-3.5" />
                                      Save as Template
                                    </h4>
                                    <TemplateSaveMetadataFields
                                      idPrefix="create-composite-template"
                                      value={createSaveForm}
                                      onFieldChange={handleCreateSaveMetadataFieldChange}
                                    />
                                    <div className="flex items-center gap-2 pt-1">
                                      {reviewCanBeAccepted && (
                                        createReviewAccepted ? (
                                          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                            {showApplyButton ? 'Review accepted. Apply and save are enabled.' : 'Review accepted. Save is enabled.'}
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => setCreateReviewAccepted(true)}
                                            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                                          >
                                            Accept Review
                                          </button>
                                        )
                                      )}
                                      {showManualEditButton && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleOpenGeneratedManualBuilder(
                                              createResult,
                                              createReviewAccepted ? futureActionOptions : currentActionOptions,
                                            )
                                          }
                                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800"
                                        >
                                          <Settings2 className="h-3.5 w-3.5" />
                                          Edit in Manual Builder
                                        </button>
                                      )}
                                      {showApplyButton && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (currentApplyPayload.ok && currentApplyPayload.payload.type === 'composite') {
                                              handleSelectComposite(currentApplyPayload.payload.selection);
                                            }
                                          }}
                                          disabled={!currentApplyPayload.ok}
                                          title={!currentApplyPayload.ok ? currentGeneratedActionState.apply ?? currentApplyPayload.disabledReason : undefined}
                                          className={getCreateActionButtonClass(true)}
                                        >
                                          <ChevronRight className="h-3.5 w-3.5" />
                                          Apply to Chart
                                        </button>
                                      )}
                                      <button
                                        onClick={handleSaveTemplate}
                                        disabled={!createSaveForm.name.trim() || isSaving || !currentGeneratedActionState.canSave}
                                        title={currentGeneratedActionState.canSave ? undefined : saveDisabledReason ?? undefined}
                                        className={getCreateActionButtonClass(saveIsPrimary)}
                                      >
                                        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                        Save Template
                                      </button>
                                      <button
                                        onClick={handleResetCreate}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Reset
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          }

                          const templatePreviewModel = createResult.response.templateQuery ? buildCreateTemplatePreviewModel(createResult) : null;
                          const reviewCanBeAccepted = canAcceptCreateReview(createResult);
                          const currentActionOptions: GeneratedCreateActionOptions = {
                            reviewAccepted: createReviewAccepted,
                            isPersonalSpace,
                            discoveryResult,
                          };
                          const futureActionOptions: GeneratedCreateActionOptions = {
                            reviewAccepted: true,
                            isPersonalSpace,
                            discoveryResult,
                          };
                          const currentGeneratedActionState = getGeneratedCreateActionState(createResult, currentActionOptions);
                          const futureGeneratedActionState = getGeneratedCreateActionState(createResult, futureActionOptions);
                          const currentApplyPayload = buildGeneratedCreateApplyPayload(createResult, currentActionOptions);
                          const futureApplyPayload = buildGeneratedCreateApplyPayload(createResult, futureActionOptions);
                          const saveDisabledReason = currentGeneratedActionState.save;
                          const showManualEditButton = currentGeneratedActionState.canSave;
                          const showApplyButton =
                            Boolean(component) &&
                            futureGeneratedActionState.canApply &&
                            futureApplyPayload.ok &&
                            futureApplyPayload.payload.type === 'single-source';
                          const showDisabledPersonalSpaceApplyButton =
                            Boolean(component) &&
                            futureGeneratedActionState.apply === PERSONAL_SPACE_SINGLE_SOURCE_SCOPE_ERROR;
                          const saveIsPrimary = !showApplyButton;
                          const fallbackReviewReason =
                            createResult.metadata.reviewReason ??
                            'This query could not be converted to a reusable template. Try specifying the data source and metrics more explicitly.';
                          const showDistinctFallbackSaveReason =
                            Boolean(saveDisabledReason) &&
                            saveDisabledReason !== fallbackReviewReason;

                          return (
                            <>
                              {/* Answer text */}
                              {createResult.response.answer && (
                                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-md p-3">
                                  <p className="text-sm text-gray-700 dark:text-gray-300">{createResult.response.answer}</p>
                                </div>
                              )}

                              {/* Template query preview */}
                              {templatePreviewModel ? (
                                <div className="border border-emerald-200 dark:border-emerald-800 rounded-md bg-emerald-50/50 dark:bg-emerald-900/20">
                                  <CreateResultPreviewCard previewModel={templatePreviewModel} needsReview={createResult.metadata.needsReview} />

                                  {/* Save form */}
                                  <div className="border-t border-emerald-200 dark:border-emerald-800 px-3 py-3 space-y-3">
                                    <h4 className="text-xs font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1.5">
                                      <Save className="h-3.5 w-3.5" />
                                      Save as Template
                                    </h4>
                                    <TemplateSaveMetadataFields
                                      idPrefix="create-template"
                                      value={createSaveForm}
                                      onFieldChange={handleCreateSaveMetadataFieldChange}
                                    />
                                    <div className="flex items-center gap-2 pt-1">
                                      {reviewCanBeAccepted && (
                                        createReviewAccepted ? (
                                          <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
                                            {showApplyButton ? 'Review accepted. Apply and save are enabled.' : 'Review accepted. Save is enabled.'}
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={() => setCreateReviewAccepted(true)}
                                            className="inline-flex items-center gap-1.5 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                                          >
                                            Accept Review
                                          </button>
                                        )
                                      )}
                                      {showManualEditButton && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleOpenGeneratedManualBuilder(
                                              createResult,
                                              createReviewAccepted ? futureActionOptions : currentActionOptions,
                                            )
                                          }
                                          className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-transparent dark:text-slate-300 dark:hover:bg-slate-800"
                                        >
                                          <Settings2 className="h-3.5 w-3.5" />
                                          Edit in Manual Builder
                                        </button>
                                      )}
                                      {(showApplyButton || showDisabledPersonalSpaceApplyButton) && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (currentApplyPayload.ok && currentApplyPayload.payload.type === 'single-source') {
                                              handleApplyQueryDefinition({
                                                queryDefinition: currentApplyPayload.payload.queryDefinition,
                                                organizationId: currentApplyPayload.payload.organizationId,
                                                organizationName: currentApplyPayload.payload.organizationName,
                                              });
                                            }
                                          }}
                                          disabled={!currentApplyPayload.ok}
                                          title={!currentApplyPayload.ok ? currentGeneratedActionState.apply ?? currentApplyPayload.disabledReason : undefined}
                                          className={getCreateActionButtonClass(showApplyButton)}
                                        >
                                          <ChevronRight className="h-3.5 w-3.5" />
                                          Apply to Chart
                                        </button>
                                      )}
                                      <button
                                        onClick={handleSaveTemplate}
                                        disabled={!createSaveForm.name.trim() || isSaving || !currentGeneratedActionState.canSave}
                                        title={currentGeneratedActionState.canSave ? undefined : saveDisabledReason ?? undefined}
                                        className={getCreateActionButtonClass(saveIsPrimary)}
                                      >
                                        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                        Save Template
                                      </button>
                                      <button
                                        onClick={handleResetCreate}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Reset
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* No structured template in response */
                                !createError && (
                                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md p-3">
                                    <div className="mb-2 flex flex-wrap items-center gap-2">
                                      <p className="text-xs font-semibold text-amber-800 dark:text-amber-300">Result type: Needs Review</p>
                                      <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
                                        Needs Review
                                      </span>
                                    </div>
                                    <p className="text-sm text-amber-800 dark:text-amber-300">
                                      {fallbackReviewReason}
                                    </p>
                                    {showDistinctFallbackSaveReason && (
                                      <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                                        {saveDisabledReason}
                                      </p>
                                    )}
                                    <div className="mt-3 flex items-center gap-2">
                                      <button
                                        type="button"
                                        disabled={!createSaveForm.name.trim() || isSaving || !currentGeneratedActionState.canSave}
                                        title={currentGeneratedActionState.canSave ? undefined : saveDisabledReason ?? undefined}
                                        className={getCreateActionButtonClass(false)}
                                      >
                                        {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                                        Save Template
                                      </button>
                                      <button
                                        type="button"
                                        onClick={handleResetCreate}
                                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                      >
                                        <RotateCcw className="h-3.5 w-3.5" />
                                        Reset
                                      </button>
                                    </div>
                                  </div>
                                )
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}

                    {/* Empty state */}
                    {!createResult && !createLoading && !createError && (
                      <div className="flex flex-col items-center justify-center flex-1 min-h-[200px] gap-3 text-center">
                        <Sparkles className="h-10 w-10 text-gray-200 dark:text-gray-700" />
                        <div>
                          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">AI Template Builder</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Describe the data you need and save it as a reusable template</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Chat input pinned to bottom */}
                  <div className="shrink-0 px-4 pb-4 pt-2">
                    <div className="rounded-[28px] border border-input bg-background shadow-sm transition-colors focus-within:border-ring">
                      {/* Textarea */}
                      <div style={{ padding: '12px 20px 0' }}>
                        <Textarea
                          ref={createInputRef}
                          aria-label="Create template prompt"
                          value={createInput}
                          onChange={(e) => setCreateInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleGenerate();
                            }
                          }}
                          placeholder="e.g., Average temperature by hour for the last 7 days"
                          disabled={createLoading}
                          className="w-full resize-none border-0 bg-transparent p-0 text-foreground shadow-none placeholder:text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                          rows={1}
                          style={{
                            fontSize: '14px',
                            lineHeight: '1.5',
                            minHeight: '24px',
                            maxHeight: '120px',
                            overflow: 'auto',
                            border: 'none',
                            outline: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                          }}
                        />
                      </div>

                      {/* Bottom toolbar */}
                      <div className="flex items-center justify-between" style={{ padding: '4px 8px 8px 12px' }}>
                        {/* Left group */}
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={createLoading}
                            aria-label="Attach"
                          >
                            <Plus className="w-5 h-5" />
                          </button>
                        </div>

                        {/* Right group */}
                        <div className="flex items-center gap-1">
                          {/* Voice mic button */}
                          <VoiceMicButton
                            voiceState={voiceState}
                            onPress={handleMicPress}
                            onStopSpeaking={handleStopSpeaking}
                            disabled={createLoading}
                            error={voiceError}
                            compact
                          />
                          {/* Send button — circular, matches editor/viewer pattern */}
                          <button
                            type="button"
                            onClick={handleGenerate}
                            disabled={!createInput.trim() || createLoading}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground"
                            aria-label="Generate template"
                          >
                            {createLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
